/**
 * Update Prompt Component
 * 
 * Shows version update prompts with different styles:
 * - Soft Update: Dismissible dialog
 * - Hard Update: Full-screen blocking modal
 * - Emergency Update: Full-screen blocking modal with urgent styling
 */

import React from 'react';
import { View, Modal, StyleSheet, Platform, Pressable } from 'react-native';
import { Theme } from '../utils/theme';
import Text from './ui/Text';
import Button from './ui/Button';
import {
  VersionCheckResult,
  UpdateStatus,
  openAppStore,
  dismissSoftUpdate,
} from '../services/versionService';

interface UpdatePromptProps {
  result: VersionCheckResult;
  onDismiss?: () => void;
}

export function UpdatePrompt({ result, onDismiss }: UpdatePromptProps) {
  const isSoft = result.status === UpdateStatus.SOFT_UPDATE;
  const isEmergency = result.status === UpdateStatus.EMERGENCY_UPDATE;
  
  if (!result.shouldShowPrompt) {
    return null;
  }
  
  const handleUpdate = async () => {
    await openAppStore(result);
  };
  
  const handleDismiss = async () => {
    if (isSoft) {
      await dismissSoftUpdate();
      onDismiss?.();
    }
  };
  
  return (
    <Modal
      visible={result.shouldShowPrompt}
      transparent
      animationType="fade"
      onRequestClose={isSoft ? handleDismiss : undefined}
    >
      <View style={styles.overlay}>
        <View style={[
          styles.container,
          isEmergency && styles.emergencyContainer,
        ]}>
          {/* Icon */}
          <View style={[
            styles.iconContainer,
            isEmergency && styles.emergencyIconContainer,
          ]}>
            <Text style={styles.icon}>
              {isEmergency ? '🚨' : isSoft ? '✨' : '⚠️'}
            </Text>
          </View>
          
          {/* Title */}
          <Text style={styles.title}>
            {isEmergency
              ? 'Critical Update Required'
              : isSoft
              ? 'Update Available'
              : 'Update Required'
            }
          </Text>
          
          {/* Message */}
          <Text style={styles.message}>
            {result.updateMessage}
          </Text>
          
          {/* Version Info */}
          <View style={styles.versionInfo}>
            <Text style={styles.versionText}>
              Current version: {result.currentVersion}
            </Text>
            {result.latestVersion && (
              <Text style={styles.versionText}>
                Latest version: {result.latestVersion}
              </Text>
            )}
          </View>
          
          {/* Actions */}
          <View style={styles.actions}>
            <Button
              title={
                Platform.OS === 'web' 
                  ? 'Refresh Page' 
                  : Platform.OS === 'ios' 
                  ? 'Update on App Store' 
                  : 'Update on Play Store'
              }
              onPress={Platform.OS === 'web' ? () => window.location.reload() : handleUpdate}
              variant="primary"
              style={[
                styles.updateButton,
                isEmergency && styles.emergencyButton,
              ]}
            />
            
            {isSoft && (
              <Pressable
                onPress={handleDismiss}
                style={styles.dismissButton}
              >
                <Text style={styles.dismissText}>
                  Remind me later
                </Text>
              </Pressable>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: Theme.spacing.xl,
  },
  container: {
    backgroundColor: Theme.colors.surface,
    borderRadius: Theme.borderRadius.lg,
    padding: Theme.spacing.xl,
    paddingTop: Theme.spacing.xxl, // Extra padding at top for icon
    maxWidth: 400,
    width: '100%',
    borderWidth: 1,
    borderColor: Theme.colors.border,
  },
  emergencyContainer: {
    borderColor: Theme.colors.error,
    borderWidth: 2,
  },
  iconContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: Theme.colors.backgroundSecondary,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    marginBottom: Theme.spacing.lg,
  },
  emergencyIconContainer: {
    backgroundColor: Theme.colors.error + '20',
  },
  icon: {
    fontSize: 32,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: Theme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: Theme.spacing.md,
  },
  message: {
    fontSize: 16,
    color: Theme.colors.textSecondary,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: Theme.spacing.lg,
  },
  versionInfo: {
    backgroundColor: Theme.colors.backgroundSecondary,
    borderRadius: Theme.borderRadius.md,
    padding: Theme.spacing.md,
    marginBottom: Theme.spacing.lg,
  },
  versionText: {
    fontSize: 14,
    color: Theme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: Theme.spacing.xs,
  },
  actions: {
    gap: Theme.spacing.md,
  },
  updateButton: {
    width: '100%',
  },
  emergencyButton: {
    backgroundColor: Theme.colors.error,
  },
  dismissButton: {
    padding: Theme.spacing.md,
    alignItems: 'center',
  },
  dismissText: {
    fontSize: 16,
    color: Theme.colors.textSecondary,
    textAlign: 'center',
  },
});

